import { type CheckoutReceipts, type Receipt, type CheckoutReceiptGetter } from '../types';
import type { Maybe } from '@tectonics/utils/types';
import type { Country } from '@tectonics/prs/country';
import { getOrThrow } from '@tectonics/utils/getters';
import { getSafeInstalmentsPlan } from './instalments-plan';

type InstalmentNumber = number | '';

const VALID_EMPTY_INSTALMENTS: readonly InstalmentNumber[] = [0, ''];

type ResolvePaymentType = (paymentMethod: string, country: Country) => string;

export const getInstalmentsFromReceipts: CheckoutReceiptGetter<ReadonlyArray<Receipt>> = ({instalments}) =>
  Object.values(instalments);

export const getInstalmentByNumber: CheckoutReceiptGetter<Maybe<Receipt>, [InstalmentNumber]> = (
  receipts: CheckoutReceipts,
  instalmentNumber: InstalmentNumber,
) => {
  const instalmentsList = getInstalmentsFromReceipts(receipts);
  const instalmentNumberToConsider = resolveInstalmentNumber(instalmentNumber);

  return instalmentsList.find(
    ({ instalments_plan }) => instalments_plan && instalments_plan.instalments === instalmentNumberToConsider
  );
};

export const getSafeInstalmentByNumber: CheckoutReceiptGetter<Receipt, [InstalmentNumber]> = (receipts, instalmentNumber) => getOrThrow(
  getInstalmentByNumber(receipts, instalmentNumber),
  `getSafeInstalmentByNumber: there is no instalment number: "${instalmentNumber}"`
);

export const getInstalmentByEndUser: CheckoutReceiptGetter<Maybe<Receipt>, [string, Country, number]> = (
  receipts,
  paymentMethod,
  country,
  instalmentNumber,
) => {
  const paymentGroup = paymentMethod;
  const paymentType = resolvePaymentType(paymentMethod, country);

  const instalmentsByNumber = receipts?.instalments_with_end_user_fee?.[paymentGroup]?.[paymentType];
  if (!instalmentsByNumber) return undefined;

  const instalmentsList: ReadonlyArray<Receipt> = Object.values(instalmentsByNumber);
  if (instalmentsList.length === 0) return undefined;

  const resolvedInstalment = resolveInstalmentNumber(instalmentNumber);

  return instalmentsList.find(
    ({ instalments_plan}) => instalments_plan?.instalments === resolvedInstalment
  );
};

const resolvePaymentType: ResolvePaymentType = (paymentMethod, country): string => 
  (country === 'ar' && paymentMethod === 'banktransfer') ? 'debin' : paymentMethod;

const resolveInstalmentNumber = (instalmentNumber: InstalmentNumber): number =>
  VALID_EMPTY_INSTALMENTS.includes(instalmentNumber) ? 1 : Number(instalmentNumber);

export const getLowerInstalmentKey: CheckoutReceiptGetter<string> = (receipts) => {
  const instalments = getInstalmentsFromReceipts(receipts)
    .map(instalment => getSafeInstalmentsPlan(instalment).instalments);

  return (instalments.length === 0) ? '1' : Math.min(...instalments).toString();
};
